import { useState } from 'react';
import {
  Box,
  Button,
  Header,
  Grid,
  Pagination,
  RadioGroup,
  Select,
  SpaceBetween,
  Spinner,
  type PropertyFilterProps,
} from '@amzn/awsui-components-react';
import type { AccountPipelineGroup, AccountPipelineOpportunity } from '@amzn/ivthub-client';
import FloatingLabelField from '../../../components/FloatingLabelField/FloatingLabelField';
import { dismissalKey, type DismissalFailure, type DismissalState } from '../useDismissal';
import {
  AccountPipelineItem,
  DEFAULT_MIN_SCORE,
  type DismissedOf,
  visibleSuggestions,
  WRITES_BUSY_REASON,
} from './AccountPipelineItem';
import { IgnoreAssetsModal, type DismissalChange } from './IgnoreAssetsModal';
import { AccountPipelinePropertyFilter } from './AccountPipelinePropertyFilter';
import type { AccountPipelineFilterOptions } from '../useAccountPipelineFilterOptions';
import { queryToAccountPipelineFilter } from '../accountPipelineFilter';

/**
 * 我的灯塔客户 / 圈外归仓 — the whole block: its title and controls, and one
 * {@link AccountPipelineItem} per account.
 *
 * `Ignore` writes through `dismissal` and then records the outcome HERE rather than refetching:
 * a refetch would drop the pager back to page one (the token is bound to the scope), and the
 * service's answer for the pair is already known — it is the write that just succeeded.
 */

export interface AccountPipelineListProps {
  accounts: AccountPipelineGroup[];
  /** Picks the block, exactly as it does on the request: `true` = 我的灯塔客户. */
  lighthouse: boolean;
  loading?: boolean;
  /** 1-based. Omit the pagination props entirely and no pager renders. */
  pageIndex?: number;
  /** Pages walked so far — the only ones a token exists for. */
  pagesCount?: number;
  hasNextPage?: boolean;
  onPageChange?: (pageIndex: number) => void;
  onNextPage?: () => void;
  /**
   * The Ignore write path. Pass it only for a user who may write: absent, no row offers Ignore.
   */
  dismissal?: Pick<DismissalState, 'dismiss' | 'restore'>;
  /**
   * `+ Motion` — claim one opportunity into a Motion. Per opportunity, the grain a claim is made
   * at; the account travels with it because the claim carries both. Pass it only for a user who
   * may write: absent, no row offers it.
   */
  onClaim?: (group: AccountPipelineGroup, opp: AccountPipelineOpportunity) => void;
  /**
   * `+ Add Lighthouse Account` (DAT-9086). The button renders ONLY when this is passed, and only
   * on the lighthouse block — the caller passes it for a caller who may write the flag, and omits
   * it otherwise, so a viewer is not shown a button that would 403.
   */
  onAdd?: () => void;
  /**
   * 🗑 Delete on each account — `DeleteAccountFlag` for LIGHTHOUSE. Same rule as `onAdd`, and
   * only on the lighthouse block. The name travels with the id so the confirmation can say who.
   */
  onDelete?: (accountId: string, accountName: string | undefined) => void;
  /** Add and every Delete are shown but disabled — while a batch of adds is still running. */
  writesDisabled?: boolean;
  /**
   * The relevance floor, as the request carries it. `null` means the member is omitted and the
   * server's own default (3) applies — so the default has exactly one home, on the server.
   */
  minScore?: number | null;
  onMinScoreChange?: (minScore: number | null) => void;
  /** `(N Account, N opps, $N)`. The title lives here so it shares the comp's first row with
   *  the view toggle. */
  counter?: string;
  /** Why there are no rows, when the reason is not "there are none". */
  failure?: 'denied' | 'error' | null;
  /**
   * The block's property filter (DAT-9223), applied by the service across every page. The
   * caller owns the query because it is request scope, like `minScore`. Absent, no filter
   * renders.
   */
  filter?: {
    query: PropertyFilterProps.Query;
    onChange: (query: PropertyFilterProps.Query) => void;
    options: AccountPipelineFilterOptions;
  };
}

export function AccountPipelineList({
  accounts,
  lighthouse,
  loading = false,
  pageIndex,
  pagesCount,
  hasNextPage = false,
  onPageChange,
  onNextPage,
  minScore = null,
  onMinScoreChange,
  onAdd,
  onDelete,
  writesDisabled = false,
  counter,
  failure = null,
  dismissal,
  onClaim,
  filter,
}: AccountPipelineListProps) {
  // One clock for the whole paint, shared by every item — see `daysAgo` in AccountPipelineItem.
  const todayMs = Date.now();
  /**
   * A refetch with the previous rows still on screen — after an add or a delete, and on any scope
   * change the hook keeps rows through. The read takes seconds on the service, and without this
   * the list sat unchanged for that long after the write had visibly finished, reading as "the
   * add did nothing". Same treatment as the page's own refetch: say so beside the title, and dim
   * the rows rather than blank them.
   */
  const refreshing = loading && accounts.length > 0;

  /**
   * The view mode sets every card at once; a card's own caret then departs from it for that card
   * alone. Switching view discards those departures, so the switch always means "all of them".
   * Keyed by account id, which is also each card's React key.
   */
  const [expanded, setExpanded] = useState(true);
  const [cardExpanded, setCardExpanded] = useState<Record<string, boolean>>({});
  /**
   * Pairs this page has written, and to what. They win over the list's `isDismissed`, which is
   * as of the fetch; a later fetch reports the same answer, so a stale entry is harmless.
   */
  const [dismissedPairs, setDismissedPairs] = useState<Readonly<Record<string, boolean>>>({});
  const dismissedOf: DismissedOf = (opptyId, assetId, onWire) =>
    dismissedPairs[dismissalKey(opptyId, assetId)] ?? onWire;
  /** The row whose Ignore dialog is open. */
  const [ignoring, setIgnoring] = useState<AccountPipelineOpportunity | null>(null);
  const floor = minScore ?? DEFAULT_MIN_SCORE;

  /**
   * Every changed pair at once — they are independent rows server-side — and resolves to the
   * failures. A success is recorded as it lands, so a partial failure still shows what took.
   */
  const writeChanges = async (opptyId: string, changes: DismissalChange[]) => {
    const failed = new Map<string, DismissalFailure>();
    await Promise.all(
      changes.map(async ({ assetId, dismissed }) => {
        const failure = await (dismissed ? dismissal!.dismiss : dismissal!.restore)(
          opptyId,
          assetId,
        );
        if (failure) failed.set(assetId, failure);
        else setDismissedPairs((m) => ({ ...m, [dismissalKey(opptyId, assetId)]: dismissed }));
      }),
    );
    return failed;
  };

  return (
    <SpaceBetween size="s">
      <Grid gridDefinition={[{ colspan: 5 }, { colspan: 4 }, { colspan: 3 }]}>
        <Header
          variant="h2"
          counter={counter}
          info={
            <span role="status" aria-live="polite">
              {refreshing && (
                <span className="ivt-dashboard__refreshing">
                  <Spinner size="normal" />
                  <Box variant="small" color="text-body-secondary">
                    更新中…
                  </Box>
                </span>
              )}
            </span>
          }
        >
          {lighthouse ? '我的灯塔客户' : '圈外归仓（非灯塔客户）'}
        </Header>
        <Box textAlign="center" padding={{ top: 'xxs' }}>
          <RadioGroup
            direction="horizontal"
            value={expanded ? 'pipeline' : 'account'}
            onChange={({ detail }) => {
              setExpanded(detail.value === 'pipeline');
              setCardExpanded({});
            }}
            items={[
              { value: 'account', label: 'Account View' },
              { value: 'pipeline', label: 'Pipeline View' },
            ]}
          />
        </Box>
        <Box float="right">
          {lighthouse && onAdd && (
            <Button
              iconName="add-plus"
              onClick={onAdd}
              disabled={writesDisabled}
              disabledReason={WRITES_BUSY_REASON}
            >
              Add Lighthouse Account
            </Button>
          )}
        </Box>
      </Grid>

      <Box fontSize="body-s" color="text-body-secondary">
        {lighthouse
          ? '*Tagged Pipeline: 只展示时间区间以前未关的pipeline; 按account / 评分 / asset排序。Untagged Pipelines: 只展示相关性分数>=3的pipeline, 全量请于Wave页查看。'
          : '只展示相关性分数达到门槛的未打标机会。'}
      </Box>

      <Grid gridDefinition={[{ colspan: 7 }, { colspan: 2 }, { colspan: 3 }]}>
        <div>
          {filter && (
            <AccountPipelinePropertyFilter
              lighthouse={lighthouse}
              query={filter.query}
              onChange={filter.onChange}
              filteringOptions={filter.options.filteringOptions}
              filteringStatusType={filter.options.status}
              onLoadItems={filter.options.onLoadItems}
            />
          )}
        </div>
        <FloatingLabelField label="Score">
          <Select
            selectedOption={
              minScore === null
                ? { value: 'all', label: '≥3（默认）' }
                : { value: String(minScore), label: `≥${minScore}` }
            }
            options={[
              { value: 'all', label: '≥3（默认）' },
              { value: '4', label: '≥4' },
              { value: '5', label: '≥5' },
            ]}
            expandToViewport
            onChange={({ detail }) =>
              onMinScoreChange?.(
                detail.selectedOption.value === 'all' ? null : Number(detail.selectedOption.value),
              )
            }
          />
        </FloatingLabelField>
        {/* Forward-only, because the token is. `openEnd` follows `hasNextPage`: a never-disabled
            next arrow promises a page that does not exist, and `pagesCount` counts only the pages
            a token is in hand for, so no numbered button points at a position that would 400. */}
        <Box float="right">
          {pageIndex !== undefined && pagesCount !== undefined && (
            <Pagination
              currentPageIndex={pageIndex}
              pagesCount={pagesCount}
              openEnd={hasNextPage}
              disabled={loading}
              ariaLabels={{
                paginationLabel: lighthouse ? '灯塔客户分页' : '圈外归仓分页',
                previousPageLabel: '上一页',
                nextPageLabel: '下一页',
                pageLabel: (n) => `第 ${n} 页`,
              }}
              onChange={({ detail }) => {
                if (detail.currentPageIndex > pagesCount) onNextPage?.();
                else onPageChange?.(detail.currentPageIndex);
              }}
            />
          )}
        </Box>
      </Grid>

      {accounts.length === 0 ? (
        <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'l' }}>
          {loading
            ? '加载中…'
            : failure === 'denied'
              ? '无权查看此区块'
              : failure === 'error'
                ? '加载失败，请重试'
                : // The filter is kept across window and industry changes, so it can be why.
                  filter && queryToAccountPipelineFilter(filter.query)
                  ? '没有符合筛选条件的客户'
                  : '没有可展示的客户'}
        </Box>
      ) : (
        <div
          className={refreshing ? 'ivt-dashboard__accounts is-stale' : 'ivt-dashboard__accounts'}
          aria-busy={refreshing}
        >
          <SpaceBetween size="s">
            {accounts.map((group) => {
              const accountId = group.account?.accountId;
              return (
                // No `?? ''`: an undefined key warns, where `''` would let two groups quietly share one.
                <AccountPipelineItem
                  key={accountId}
                  group={group}
                  expanded={cardExpanded[accountId ?? ''] ?? expanded}
                  onExpandedChange={(next) => setCardExpanded((m) => ({ ...m, [accountId ?? '']: next }))}
                  minScore={minScore}
                  dismissedOf={dismissedOf}
                  onIgnore={dismissal ? setIgnoring : undefined}
                  onClaim={onClaim ? (opp) => onClaim(group, opp) : undefined}
                  todayMs={todayMs}
                  deleteDisabled={writesDisabled}
                  // Lighthouse block only, and only with an id: without one there is nothing to
                  // address the write to.
                  onDelete={
                    lighthouse && onDelete && accountId
                      ? () => onDelete(accountId, group.account?.accountName)
                      : undefined
                  }
                />
              );
            })}
          </SpaceBetween>
        </div>
      )}

      {ignoring && (
        <IgnoreAssetsModal
          // Keyed by row, so reopening on another row starts from that row's state.
          key={ignoring.opptyId}
          oppName={ignoring.oppName ?? ignoring.opptyId ?? ''}
          candidates={visibleSuggestions(ignoring, floor).map((s) => ({
            assetId: s.assetId,
            name: s.assetName ?? s.assetId,
            score: s.relevanceScore,
            dismissed: dismissedOf(ignoring.opptyId ?? '', s.assetId, s.isDismissed === true),
          }))}
          onSubmit={(changes) => writeChanges(ignoring.opptyId ?? '', changes)}
          onClose={() => setIgnoring(null)}
        />
      )}
    </SpaceBetween>
  );
}
